import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
  Alert,
} from '@mui/material';
import {
  CalendarToday,
  Person,
  FileDownload,
  ViewList,
  ViewModule,
  AccessTime,
  Business,
  Info,
} from '@mui/icons-material';
import api from '../services/api';

const STAFF_ROLES = {
  lead: { label: 'Lead', color: '#1976d2' },
  assistant: { label: 'Assistant', color: '#2e7d32' },
  cashier: { label: 'Cashier', color: '#ed6c02' },
  security: { label: 'Security', color: '#d32f2f' },
  setup: { label: 'Setup Crew', color: '#9c27b0' },
};

const StaffSchedule = () => {
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [staffMembers, setStaffMembers] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);

  const [filters, setFilters] = useState({
    staffId: '',
    dateFrom: '',
    dateTo: '',
    role: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allAssignments]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchStaffMembers(),
        fetchAllAssignments(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const response = await api.get('/staff');
      setStaffMembers(response.data?.staff || response.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchAllAssignments = async () => {
    try {
      const response = await api.get('/staff/schedule');
      setAllAssignments(response.data?.assignments || response.data || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...allAssignments];

    if (filters.staffId) {
      filtered = filtered.filter(a => a.staff_id === parseInt(filters.staffId));
    }

    if (filters.role) {
      filtered = filtered.filter(a => a.role === filters.role);
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(a => new Date(a.sale_date) >= fromDate);
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filtered = filtered.filter(a => new Date(a.sale_date) <= toDate);
    }

    filtered.sort((a, b) => new Date(a.sale_date) - new Date(b.sale_date));
    setFilteredAssignments(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleClearFilters = () => {
    setFilters({
      staffId: '',
      dateFrom: '',
      dateTo: '',
      role: '',
    });
  };

  const getStaffName = (staffId) => {
    const staff = staffMembers.find(s => s.id === staffId);
    return staff ? `${staff.first_name} ${staff.last_name}` : 'Unknown';
  };

  const getStaffPosition = (staffId) => {
    const staff = staffMembers.find(s => s.id === staffId);
    return staff?.position || 'Staff';
  };

  const getUpcomingAssignments = () => {
    const now = new Date();
    return filteredAssignments
      .filter(a => new Date(a.sale_date) >= now)
      .slice(0, 10);
  };

  const getAssignmentsByStaff = () => {
    const byStaff = {};
    filteredAssignments.forEach(assignment => {
      if (!byStaff[assignment.staff_id]) {
        byStaff[assignment.staff_id] = [];
      }
      byStaff[assignment.staff_id].push(assignment);
    });
    return byStaff;
  };

  const exportToCSV = () => {
    const headers = ['Staff Name', 'Role', 'Sale Name', 'Date', 'Start Time', 'End Time', 'Notes'];
    const rows = filteredAssignments.map(a => [
      getStaffName(a.staff_id),
      STAFF_ROLES[a.role]?.label || a.role,
      a.sale_name || 'N/A',
      new Date(a.sale_date).toLocaleDateString(),
      a.start_time,
      a.end_time,
      a.notes || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `staff-schedule-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Staff Schedule
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage staff assignments across all sales
          </Typography>
        </div>
        <Box display="flex" gap={1} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="list">
              <Tooltip title="List View">
                <ViewList />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="staff">
              <Tooltip title="By Staff">
                <ViewModule />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={exportToCSV}
            disabled={filteredAssignments.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Staff Member</InputLabel>
                <Select
                  value={filters.staffId}
                  label="Staff Member"
                  onChange={(e) => handleFilterChange('staffId', e.target.value)}
                >
                  <MenuItem value="">All Staff</MenuItem>
                  {staffMembers.map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.first_name} {staff.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {Object.entries(STAFF_ROLES).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      {config.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ height: 56 }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Assignments
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {filteredAssignments.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Active Staff
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {new Set(filteredAssignments.map(a => a.staff_id)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upcoming Shifts
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {getUpcomingAssignments().length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Sales Scheduled
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {new Set(filteredAssignments.map(a => a.sale_id)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Content */}
      {filteredAssignments.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CalendarToday sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No assignments found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or add staff assignments to sales
            </Typography>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Upcoming Assignments
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Staff Member</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Sale</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Shift Time</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getUpcomingAssignments().map((assignment, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {getStaffName(assignment.staff_id)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getStaffPosition(assignment.staff_id)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STAFF_ROLES[assignment.role]?.label || assignment.role}
                          size="small"
                          sx={{
                            bgcolor: STAFF_ROLES[assignment.role]?.color || '#757575',
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Business fontSize="small" color="action" />
                          <Typography variant="body2">
                            {assignment.sale_name || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">
                            {new Date(assignment.sale_date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="body2">
                            {assignment.start_time} - {assignment.end_time}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {assignment.notes ? (
                          <Tooltip title={assignment.notes}>
                            <IconButton size="small">
                              <Info fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {Object.entries(getAssignmentsByStaff()).map(([staffId, assignments]) => (
            <Grid item xs={12} md={6} lg={4} key={staffId}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Person color="primary" />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {getStaffName(parseInt(staffId))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getStaffPosition(parseInt(staffId))} - {assignments.length} assignments
                      </Typography>
                    </Box>
                  </Box>

                  {assignments.slice(0, 5).map((assignment, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          {assignment.sale_name || 'Sale'}
                        </Typography>
                        <Chip
                          label={STAFF_ROLES[assignment.role]?.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: STAFF_ROLES[assignment.role]?.color,
                            color: 'white',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {new Date(assignment.sale_date).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {assignment.start_time} - {assignment.end_time}
                      </Typography>
                    </Box>
                  ))}

                  {assignments.length > 5 && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                      + {assignments.length - 5} more assignments
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default StaffSchedule;
